import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'en' | 'hi';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation strings
const translations = {
  en: {
    // Common
    'common.refresh': 'Refresh',
    'common.autoRefresh': 'Auto-refresh',
    'common.on': 'ON',
    'common.off': 'OFF',
    'common.lastUpdated': 'Last updated',
    'common.loading': 'Loading',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.actions': 'Actions',
    'common.status': 'Status',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.create': 'Create',
    'common.update': 'Update',
    'common.close': 'Close',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.active': 'Active',
    'common.inactive': 'Inactive',

    // Admin Dashboard
    'dashboard.title': 'Admin Dashboard',
    'dashboard.subtitle': 'Platform overview and system health monitoring',
    'dashboard.cpuLoad': 'CPU Load',
    'dashboard.heapMemory': 'Heap Memory',
    'dashboard.activeThreads': 'Active Threads',
    'dashboard.systemUptime': 'System Uptime',
    'dashboard.processorsAvailable': 'processors available',
    'dashboard.peak': 'Peak',
    'dashboard.sinceLastRestart': 'Since last restart',

    // Microservices
    'services.title': 'Microservices Health Status',
    'services.subtitle': 'Real-time status of all platform microservices',
    'services.online': 'services online',
    'services.authService': 'Auth Service',
    'services.iotService': 'IoT Service',
    'services.biogasService': 'Biogas Service',
    'services.commerceService': 'Commerce Service',
    'services.reportingService': 'Reporting Service',
    'services.governmentService': 'Government Service',

    // JVM
    'jvm.title': 'JVM Information',
    'jvm.subtitle': 'Java Virtual Machine details',
    'jvm.totalMemory': 'Total Memory',
    'jvm.freeMemory': 'Free Memory',
    'jvm.usedMemory': 'Used Memory',
    'jvm.maxMemory': 'Max Memory',

    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.userManagement': 'User Management',
    'nav.deviceRegistry': 'Device Registry',
    'nav.auditLogs': 'Audit Logs',
    'nav.reports': 'Reports',
    'nav.accessManagement': 'Access Management',
    'nav.configuration': 'Configuration',
    'nav.profile': 'Profile',
    'nav.admin': 'Admin',
    'nav.platformManagement': 'Platform Management',
    'nav.systemOnline': 'System Online',

    // USER MANAGEMENT MODULE
    'userManagement.title': 'User Management',
    'userManagement.subtitle': 'Manage users, roles, and permissions across the platform',
    'userManagement.addUserButton': 'Add User',
    'userManagement.searchPlaceholder': 'Search by name, phone, or email...',
    'userManagement.searchButton': 'Search',
    'userManagement.totalUsers': 'Total Users',
    'userManagement.activeUsers': 'Active Users',
    'userManagement.currentPage': 'Current Page',
    'userManagement.table.name': 'Name',
    'userManagement.table.phone': 'Phone',
    'userManagement.table.email': 'Email',
    'userManagement.table.roles': 'Roles',
    'userManagement.table.status': 'Status',
    'userManagement.table.actions': 'Actions',
    'userManagement.action.toggleStatus': 'Toggle Status',
    'userManagement.action.edit': 'Edit User',
    'userManagement.action.resetPassword': 'Reset Password',
    'userManagement.action.delete': 'Delete User',
    'userManagement.action.previous': 'Previous',
    'userManagement.action.next': 'Next',
    'userManagement.action.showing': 'Showing',
    'userManagement.addUser.title': 'Add New User',
    'userManagement.addUser.description': 'Create a new user account with role assignment',
    'userManagement.addUser.phone': 'Phone Number',
    'userManagement.addUser.phoneFormat': 'Format: +91XXXXXXXXXX (Indian mobile number)',
    'userManagement.addUser.name': 'Full Name',
    'userManagement.addUser.nameHint': '3-50 characters, letters and spaces only',
    'userManagement.addUser.password': 'Password',
    'userManagement.addUser.passwordHint': '8-20 characters with uppercase, lowercase, number & special character',
    'userManagement.addUser.userType': 'User Type / Role',
    'userManagement.addUser.locale': 'Language / Locale',
    'userManagement.addUser.create': 'Create User',
    'userManagement.editUser.title': 'Edit User',
    'userManagement.editUser.description': 'Update user information including roles and permissions',
    'userManagement.editUser.currentRoles': 'Current Roles',
    'userManagement.editUser.addRole': 'Add Role',
    'userManagement.editUser.accountStatus': 'Account Status',
    'userManagement.editUser.update': 'Update User',
    'userManagement.resetPassword.title': 'Reset User Password',
    'userManagement.resetPassword.user': 'User',
    'userManagement.resetPassword.phone': 'Phone',
    'userManagement.resetPassword.newPassword': 'New Password',
    'userManagement.resetPassword.confirmPassword': 'Confirm Password',
    'userManagement.resetPassword.warning': 'Warning: This will immediately change the user\'s password',
    'userManagement.resetPassword.submit': 'Reset Password',
    'userManagement.validation.phoneRequired': 'Phone number is required',
    'userManagement.validation.phoneFormat': 'Phone must be in format +91XXXXXXXXXX',
    'userManagement.validation.nameRequired': 'Name must be at least 3 characters',
    'userManagement.validation.nameMax': 'Name must not exceed 50 characters',
    'userManagement.validation.passwordRequired': 'Password is required',
    'userManagement.toast.userStatusUpdated': 'User status updated',
    'userManagement.toast.userDeleted': 'User deleted successfully',
    'userManagement.toast.userCreated': 'User created successfully',
    'userManagement.toast.userUpdated': 'User updated successfully',
    'userManagement.toast.passwordReset': 'Password reset successfully',

    // AUDIT LOGS MODULE
    'auditLogs.title': 'Audit Logs',
    'auditLogs.subtitle': 'Comprehensive audit trail of all system activities',
    'auditLogs.exportButton': 'Export CSV',
    'auditLogs.exporting': 'Exporting...',
    'auditLogs.filterButton': 'Show Filters',
    'auditLogs.hideFilters': 'Hide Filters',
    'auditLogs.clearFilters': 'Clear All Filters',
    'auditLogs.searchCard.title': 'Search & Filter',
    'auditLogs.searchCard.description': 'total audit log(s)',
    'auditLogs.searchPlaceholder': 'Search by username or details...',
    'auditLogs.filter.module': 'Module',
    'auditLogs.filter.action': 'Action',
    'auditLogs.filter.status': 'Status',
    'auditLogs.filter.startDate': 'Start Date',
    'auditLogs.filter.endDate': 'End Date',
    'auditLogs.filter.allModules': 'All Modules',
    'auditLogs.filter.allActions': 'All Actions',
    'auditLogs.filter.allStatus': 'All Status',
    'auditLogs.table.timestamp': 'Timestamp',
    'auditLogs.table.module': 'Module',
    'auditLogs.table.action': 'Action',
    'auditLogs.table.username': 'Username',
    'auditLogs.table.status': 'Status',
    'auditLogs.table.ipAddress': 'IP Address',
    'auditLogs.table.details': 'Details',
    'auditLogs.pagination.showing': 'Showing entries',
    'auditLogs.pagination.page': 'Page',
    'auditLogs.status.success': 'SUCCESS',
    'auditLogs.status.failed': 'FAILED',
    'auditLogs.status.warning': 'WARNING',
    'auditLogs.empty': 'No audit logs found. Try adjusting your filters.',
    'auditLogs.toast.preparing': 'Preparing CSV export...',
    'auditLogs.toast.exported': 'Successfully exported audit logs',
    'auditLogs.toast.exportFailed': 'Failed to export audit logs.',
    'auditLogs.toast.loadFailed': 'Failed to load audit logs. Please ensure the Reporting Service is running.',

    // DEVICE REGISTRY MODULE
    'deviceRegistry.title': 'Device Registry',
    'deviceRegistry.subtitle': 'Manage IoT devices, RFID readers, sensors, and tracking units',
    'deviceRegistry.addButton': 'Add Device',
    'deviceRegistry.health.totalDevices': 'Total Devices',
    'deviceRegistry.health.onlineOffline': 'Online / Offline',
    'deviceRegistry.health.connectivity': 'Device connectivity status',
    'deviceRegistry.health.maintenance': 'Maintenance',
    'deviceRegistry.health.avgTrustScore': 'Avg Trust Score',
    'deviceRegistry.filter.title': 'Filter Devices',
    'deviceRegistry.filter.description': 'total device(s)',
    'deviceRegistry.filter.deviceType': 'Device Type',
    'deviceRegistry.filter.allTypes': 'All Types',
    'deviceRegistry.filter.status': 'Status',
    'deviceRegistry.filter.allStatus': 'All Status',
    'deviceRegistry.filter.clusterId': 'Cluster ID',
    'deviceRegistry.filter.clearFilters': 'Clear Filters',
    'deviceRegistry.filter.showFilters': 'Show Filters',
    'deviceRegistry.filter.hideFilters': 'Hide Filters',
    'deviceRegistry.deviceType.weighingScale': 'Weighing Scale',
    'deviceRegistry.deviceType.flowMeter': 'Flow Meter',
    'deviceRegistry.deviceType.ch4Sensor': 'Methane Sensor',
    'deviceRegistry.deviceType.h2sSensor': 'H2S Sensor',
    'deviceRegistry.deviceType.rfidReader': 'RFID Reader',
    'deviceRegistry.deviceType.gpsTracker': 'GPS Tracker',
    'deviceRegistry.status.active': 'ACTIVE',
    'deviceRegistry.status.inactive': 'INACTIVE',
    'deviceRegistry.status.maintenance': 'MAINTENANCE',
    'deviceRegistry.status.offline': 'OFFLINE',
    'deviceRegistry.table.deviceId': 'Device ID',
    'deviceRegistry.table.type': 'Type',
    'deviceRegistry.table.serialNumber': 'Serial Number',
    'deviceRegistry.table.status': 'Status',
    'deviceRegistry.table.firmware': 'Firmware',
    'deviceRegistry.table.trustScore': 'Trust Score',
    'deviceRegistry.table.lastCalibration': 'Last Calibration',
    'deviceRegistry.table.cluster': 'Cluster',
    'deviceRegistry.table.actions': 'Actions',
    'deviceRegistry.action.view': 'View Details',
    'deviceRegistry.action.edit': 'Edit Device',
    'deviceRegistry.action.delete': 'Delete Device',
    'deviceRegistry.addDevice.title': 'Add New Device',
    'deviceRegistry.addDevice.description': 'Register a new IoT device in the system',
    'deviceRegistry.addDevice.deviceId': 'Device ID',
    'deviceRegistry.addDevice.serialNumber': 'Serial Number',
    'deviceRegistry.addDevice.deviceType': 'Device Type',
    'deviceRegistry.addDevice.status': 'Status',
    'deviceRegistry.addDevice.firmwareVersion': 'Firmware Version',
    'deviceRegistry.addDevice.clusterId': 'Cluster ID',
    'deviceRegistry.addDevice.trustScore': 'Trust Score (0-100)',
    'deviceRegistry.addDevice.location': 'Location',
    'deviceRegistry.addDevice.create': 'Create Device',
    'deviceRegistry.editDevice.title': 'Edit Device',
    'deviceRegistry.editDevice.description': 'Update device information',
    'deviceRegistry.editDevice.update': 'Update Device',
    'deviceRegistry.viewDevice.title': 'Device Details',
    'deviceRegistry.viewDevice.description': 'Complete information for device',
    'deviceRegistry.pagination.showing': 'Showing devices',
    'deviceRegistry.pagination.page': 'Page',
    'deviceRegistry.loading': 'Loading devices...',
    'deviceRegistry.empty': 'No devices found. Try adjusting your filters or ensure the IoT Service is running.',
    'deviceRegistry.toast.deviceCreated': 'Device created successfully',
    'deviceRegistry.toast.deviceUpdated': 'Device updated successfully',
    'deviceRegistry.toast.deviceDeleted': 'Device deleted successfully',
    'deviceRegistry.toast.loadFailed': 'Failed to load devices. Please ensure the IoT Service is running.',

    // PROFILE MODULE
    'profile.title': 'User Profile',
    'profile.subtitle': 'Manage your account information and preferences',
    'profile.editProfile': 'Edit Profile',
    'profile.editableSection': 'Editable Information',
    'profile.name': 'Full Name',
    'profile.locale': 'Language/Locale',
    'profile.readOnlySection': 'Account Information (Read-Only)',
    'profile.phone': 'Phone Number',
    'profile.email': 'Email Address',
    'profile.userId': 'User ID',
    'profile.externalId': 'External ID',
    'profile.kycStatus': 'KYC Status',
    'profile.accountStatus': 'Account Status',
    'profile.lastLogin': 'Last Login',
    'profile.createdAt': 'Account Created',
    'profile.roles': 'Roles',
    'profile.permissions': 'Permissions',
    'profile.governmentAccess': 'Government Access',
    'profile.noRolesAssigned': 'No roles assigned',
    'profile.noPermissionsAssigned': 'No permissions assigned',
    'profile.noGovernmentAccess': 'No government access',
    'profile.form.save': 'Save Changes',
    'profile.form.cancel': 'Cancel',
    'profile.form.saving': 'Saving...',
    'profile.locale.en': 'English (en)',
    'profile.locale.hi': 'हिंदी (hi)',
    'profile.locale.gu': 'ગુજરાતી (gu)',
    'profile.locale.mr': 'मराठी (mr)',
    'profile.loading': 'Loading profile...',
    'profile.error.title': 'Error Loading Profile',
    'profile.error.retry': 'Retry',
    'profile.toast.updated': 'Profile updated successfully!',
    'profile.toast.error': 'Failed to update profile',

    // REPORTS MODULE
    'reports.title': 'Revenue & Carbon Reports',
    'reports.subtitle': 'Comprehensive financial and environmental impact reporting',
    'reports.notAvailable': 'Reports Not Available',
    'reports.message': 'Revenue and operational reports require Reporting Service endpoints.',

    // CONFIGURATION MODULE
    'configuration.title': 'System Configuration',
    'configuration.subtitle': 'Manage platform settings, integrations, and security policies',
    'configuration.notAvailable': 'Configuration Not Available',
    'configuration.message': 'System configuration requires the Government Service endpoints.',

    // Locale Options
    'locale.en': 'English',
    'locale.hi': 'Hindi (हिंदी)',
    'locale.mr': 'Marathi (मराठी)',
    'locale.gu': 'Gujarati (ગુજરાતી)'
  },
  hi: {
    // Common
    'common.refresh': 'रीफ्रेश करें',
    'common.autoRefresh': 'ऑटो-रीफ्रेश',
    'common.on': 'चालू',
    'common.off': 'बंद',
    'common.lastUpdated': 'अंतिम अपडेट',
    'common.loading': 'लोड हो रहा है',
    'common.search': 'खोजें',
    'common.filter': 'फ़िल्टर करें',
    'common.actions': 'कार्यवाही',
    'common.status': 'स्थिति',
    'common.edit': 'संपादित करें',
    'common.delete': 'हटाएं',
    'common.cancel': 'रद्द करें',
    'common.save': 'सहेजें',
    'common.create': 'बनाएं',
    'common.update': 'अपडेट करें',
    'common.close': 'बंद करें',
    'common.next': 'अगला',
    'common.previous': 'पिछला',
    'common.active': 'सक्रिय',
    'common.inactive': 'निष्क्रिय',

    // Admin Dashboard
    'dashboard.title': 'एडमिन डैशबोर्ड',
    'dashboard.subtitle': 'प्लेटफ़ॉर्म अवलोकन और सिस्टम स्वास्थ्य निगरानी',
    'dashboard.cpuLoad': 'सीपीयू लोड',
    'dashboard.heapMemory': 'हीप मेमोरी',
    'dashboard.activeThreads': 'सक्रिय थ्रेड्स',
    'dashboard.systemUptime': 'सिस्टम अपटाइम',
    'dashboard.processorsAvailable': 'प्रोसेसर उपलब्ध',
    'dashboard.peak': 'चरम',
    'dashboard.sinceLastRestart': 'अंतिम रीस्टार्ट के बाद से',

    // Microservices
    'services.title': 'माइक्रोसर्विसेज स्वास्थ्य स्थिति',
    'services.subtitle': 'सभी प्लेटफ़ॉर्म माइक्रोसर्विसेज की वास्तविक समय स्थिति',
    'services.online': 'सेवाएं ऑनलाइन',
    'services.authService': 'प्रमाणीकरण सेवा',
    'services.iotService': 'आईओटी सेवा',
    'services.biogasService': 'बायोगैस सेवा',
    'services.commerceService': 'वाणिज्य सेवा',
    'services.reportingService': 'रिपोर्टिंग सेवा',
    'services.governmentService': 'सरकारी सेवा',

    // JVM
    'jvm.title': 'JVM जानकारी',
    'jvm.subtitle': 'जावा वर्चुअल मशीन विवरण',
    'jvm.totalMemory': 'कुल मेमोरी',
    'jvm.freeMemory': 'मुक्त मेमोरी',
    'jvm.usedMemory': 'उपयोग की गई मेमोरी',
    'jvm.maxMemory': 'अधिकतम मेमोरी',

    // Navigation
    'nav.dashboard': 'डैशबोर्ड',
    'nav.userManagement': 'उपयोगकर्ता प्रबंधन',
    'nav.deviceRegistry': 'डिवाइस रजिस्ट्री',
    'nav.auditLogs': 'ऑडिट लॉग',
    'nav.reports': 'रिपोर्ट्स',
    'nav.accessManagement': 'एक्सेस प्रबंधन',
    'nav.configuration': 'कॉन्फ़िगरेशन',
    'nav.profile': 'प्रोफ़ाइल',
    'nav.admin': 'एडमिन',
    'nav.platformManagement': 'प्लेटफ़ॉर्म प्रबंधन',
    'nav.systemOnline': 'सिस्टम ऑनलाइन',

    // USER MANAGEMENT MODULE
    'userManagement.title': 'उपयोगकर्ता प्रबंधन',
    'userManagement.subtitle': 'पूरे प्लेटफ़ॉर्म में उपयोगकर्ताओं, भूमिकाओं और अनुमतियों का प्रबंधन करें',
    'userManagement.addUserButton': 'उपयोगकर्ता जोड़ें',
    'userManagement.searchPlaceholder': 'नाम, फ़ोन या ईमेल द्वारा खोजें...',
    'userManagement.searchButton': 'खोजें',
    'userManagement.totalUsers': 'कुल उपयोगकर्ता',
    'userManagement.activeUsers': 'सक्रिय उपयोगकर्ता',
    'userManagement.currentPage': 'वर्तमान पृष्ठ',
    'userManagement.table.name': 'नाम',
    'userManagement.table.phone': 'फ़ोन',
    'userManagement.table.email': 'ईमेल',
    'userManagement.table.roles': 'भूमिकाएं',
    'userManagement.table.status': 'स्थिति',
    'userManagement.table.actions': 'कार्यवाही',
    'userManagement.action.toggleStatus': 'स्थिति बदलें',
    'userManagement.action.edit': 'उपयोगकर्ता को संपादित करें',
    'userManagement.action.resetPassword': 'पासवर्ड रीसेट करें',
    'userManagement.action.delete': 'उपयोगकर्ता हटाएं',
    'userManagement.action.previous': 'पिछला',
    'userManagement.action.next': 'अगला',
    'userManagement.action.showing': 'दिखा रहे हैं',
    'userManagement.addUser.title': 'नया उपयोगकर्ता जोड़ें',
    'userManagement.addUser.description': 'भूमिका असाइनमेंट के साथ एक नया उपयोगकर्ता खाता बनाएं',
    'userManagement.addUser.phone': 'फ़ोन नंबर',
    'userManagement.addUser.phoneFormat': 'प्रारूप: +91XXXXXXXXXX (भारतीय मोबाइल नंबर)',
    'userManagement.addUser.name': 'पूरा नाम',
    'userManagement.addUser.nameHint': '3-50 वर्ण, केवल अक्षर और स्थान',
    'userManagement.addUser.password': 'पासवर्ड',
    'userManagement.addUser.passwordHint': '8-20 वर्ण अपरकेस, लोअरकेस, संख्या और विशेष वर्ण के साथ',
    'userManagement.addUser.userType': 'उपयोगकर्ता प्रकार / भूमिका',
    'userManagement.addUser.locale': 'भाषा / लोकेल',
    'userManagement.addUser.create': 'उपयोगकर्ता बनाएं',
    'userManagement.editUser.title': 'उपयोगकर्ता को संपादित करें',
    'userManagement.editUser.description': 'भूमिकाओं और अनुमतियों सहित उपयोगकर्ता जानकारी अपडेट करें',
    'userManagement.editUser.currentRoles': 'वर्तमान भूमिकाएं',
    'userManagement.editUser.addRole': 'भूमिका जोड़ें',
    'userManagement.editUser.accountStatus': 'खाता स्थिति',
    'userManagement.editUser.update': 'उपयोगकर्ता अपडेट करें',
    'userManagement.resetPassword.title': 'उपयोगकर्ता पासवर्ड रीसेट करें',
    'userManagement.resetPassword.user': 'उपयोगकर्ता',
    'userManagement.resetPassword.phone': 'फ़ोन',
    'userManagement.resetPassword.newPassword': 'नया पासवर्ड',
    'userManagement.resetPassword.confirmPassword': 'पासवर्ड की पुष्टि करें',
    'userManagement.resetPassword.warning': 'चेतावनी: यह उपयोगकर्ता के पासवर्ड को तुरंत बदल देगा',
    'userManagement.resetPassword.submit': 'पासवर्ड रीसेट करें',
    'userManagement.validation.phoneRequired': 'फ़ोन नंबर आवश्यक है',
    'userManagement.validation.phoneFormat': 'फ़ोन +91XXXXXXXXXX प्रारूप में होना चाहिए',
    'userManagement.validation.nameRequired': 'नाम कम से कम 3 वर्ण होना चाहिए',
    'userManagement.validation.nameMax': 'नाम 50 वर्ण से अधिक नहीं हो सकता',
    'userManagement.validation.passwordRequired': 'पासवर्ड आवश्यक है',
    'userManagement.toast.userStatusUpdated': 'उपयोगकर्ता स्थिति अपडेट की गई',
    'userManagement.toast.userDeleted': 'उपयोगकर्ता सफलतापूर्वक हटाया गया',
    'userManagement.toast.userCreated': 'उपयोगकर्ता सफलतापूर्वक बनाया गया',
    'userManagement.toast.userUpdated': 'उपयोगकर्ता सफलतापूर्वक अपडेट किया गया',
    'userManagement.toast.passwordReset': 'पासवर्ड सफलतापूर्वक रीसेट किया गया',

    // AUDIT LOGS MODULE
    'auditLogs.title': 'ऑडिट लॉग',
    'auditLogs.subtitle': 'सभी सिस्टम गतिविधियों का व्यापक ऑडिट ट्रेल',
    'auditLogs.exportButton': 'CSV निर्यात करें',
    'auditLogs.exporting': 'निर्यात किया जा रहा है...',
    'auditLogs.filterButton': 'फ़िल्टर दिखाएं',
    'auditLogs.hideFilters': 'फ़िल्टर छिपाएं',
    'auditLogs.clearFilters': 'सभी फ़िल्टर साफ़ करें',
    'auditLogs.searchCard.title': 'खोजें और फ़िल्टर करें',
    'auditLogs.searchCard.description': 'कुल ऑडिट लॉग',
    'auditLogs.searchPlaceholder': 'उपयोगकर्ता नाम या विवरण द्वारा खोजें...',
    'auditLogs.filter.module': 'मॉड्यूल',
    'auditLogs.filter.action': 'कार्रवाई',
    'auditLogs.filter.status': 'स्थिति',
    'auditLogs.filter.startDate': 'प्रारंभ तारीख',
    'auditLogs.filter.endDate': 'समाप्ति तारीख',
    'auditLogs.filter.allModules': 'सभी मॉड्यूल',
    'auditLogs.filter.allActions': 'सभी कार्यवाही',
    'auditLogs.filter.allStatus': 'सभी स्थिति',
    'auditLogs.table.timestamp': 'समय मुहूर्त',
    'auditLogs.table.module': 'मॉड्यूल',
    'auditLogs.table.action': 'कार्रवाई',
    'auditLogs.table.username': 'उपयोगकर्ता नाम',
    'auditLogs.table.status': 'स्थिति',
    'auditLogs.table.ipAddress': 'IP पता',
    'auditLogs.table.details': 'विवरण',
    'auditLogs.pagination.showing': 'प्रविष्टियां दिखा रहे हैं',
    'auditLogs.pagination.page': 'पृष्ठ',
    'auditLogs.status.success': 'सफलता',
    'auditLogs.status.failed': 'विफल',
    'auditLogs.status.warning': 'चेतावनी',
    'auditLogs.empty': 'कोई ऑडिट लॉग नहीं मिला। अपने फ़िल्टर को समायोजित करने का प्रयास करें।',
    'auditLogs.toast.preparing': 'CSV निर्यात तैयार किया जा रहा है...',
    'auditLogs.toast.exported': 'ऑडिट लॉग सफलतापूर्वक निर्यात किए गए',
    'auditLogs.toast.exportFailed': 'ऑडिट लॉग निर्यात करने में विफल।',
    'auditLogs.toast.loadFailed': 'ऑडिट लॉग लोड करने में विफल। कृपया सुनिश्चित करें कि रिपोर्टिंग सेवा चल रही है।',

    // DEVICE REGISTRY MODULE
    'deviceRegistry.title': 'डिवाइस रजिस्ट्री',
    'deviceRegistry.subtitle': 'IoT डिवाइस, RFID रीडर, सेंसर और ट्रैकिंग यूनिट का प्रबंधन करें',
    'deviceRegistry.addButton': 'डिवाइस जोड़ें',
    'deviceRegistry.health.totalDevices': 'कुल डिवाइस',
    'deviceRegistry.health.onlineOffline': 'ऑनलाइन / ऑफलाइन',
    'deviceRegistry.health.connectivity': 'डिवाइस कनेक्टिविटी स्थिति',
    'deviceRegistry.health.maintenance': 'रखरखाव',
    'deviceRegistry.health.avgTrustScore': 'औसत ट्रस्ट स्कोर',
    'deviceRegistry.filter.title': 'डिवाइस फ़िल्टर करें',
    'deviceRegistry.filter.description': 'कुल डिवाइस',
    'deviceRegistry.filter.deviceType': 'डिवाइस प्रकार',
    'deviceRegistry.filter.allTypes': 'सभी प्रकार',
    'deviceRegistry.filter.status': 'स्थिति',
    'deviceRegistry.filter.allStatus': 'सभी स्थिति',
    'deviceRegistry.filter.clusterId': 'क्लस्टर ID',
    'deviceRegistry.filter.clearFilters': 'फ़िल्टर साफ़ करें',
    'deviceRegistry.filter.showFilters': 'फ़िल्टर दिखाएं',
    'deviceRegistry.filter.hideFilters': 'फ़िल्टर छिपाएं',
    'deviceRegistry.deviceType.weighingScale': 'तराजू',
    'deviceRegistry.deviceType.flowMeter': 'प्रवाह मीटर',
    'deviceRegistry.deviceType.ch4Sensor': 'मीथेन सेंसर',
    'deviceRegistry.deviceType.h2sSensor': 'H2S सेंसर',
    'deviceRegistry.deviceType.rfidReader': 'RFID रीडर',
    'deviceRegistry.deviceType.gpsTracker': 'जीपीएस ट्रैकर',
    'deviceRegistry.status.active': 'सक्रिय',
    'deviceRegistry.status.inactive': 'निष्क्रिय',
    'deviceRegistry.status.maintenance': 'रखरखाव',
    'deviceRegistry.status.offline': 'ऑफलाइन',
    'deviceRegistry.table.deviceId': 'डिवाइस ID',
    'deviceRegistry.table.type': 'प्रकार',
    'deviceRegistry.table.serialNumber': 'क्रम संख्या',
    'deviceRegistry.table.status': 'स्थिति',
    'deviceRegistry.table.firmware': 'फर्मवेयर',
    'deviceRegistry.table.trustScore': 'ट्रस्ट स्कोर',
    'deviceRegistry.table.lastCalibration': 'अंतिम अंशांकन',
    'deviceRegistry.table.cluster': 'क्लस्टर',
    'deviceRegistry.table.actions': 'कार्यवाही',
    'deviceRegistry.action.view': 'विवरण देखें',
    'deviceRegistry.action.edit': 'डिवाइस को संपादित करें',
    'deviceRegistry.action.delete': 'डिवाइस हटाएं',
    'deviceRegistry.addDevice.title': 'नया डिवाइस जोड़ें',
    'deviceRegistry.addDevice.description': 'सिस्टम में एक नया IoT डिवाइस पंजीकृत करें',
    'deviceRegistry.addDevice.deviceId': 'डिवाइस ID',
    'deviceRegistry.addDevice.serialNumber': 'क्रम संख्या',
    'deviceRegistry.addDevice.deviceType': 'डिवाइस प्रकार',
    'deviceRegistry.addDevice.status': 'स्थिति',
    'deviceRegistry.addDevice.firmwareVersion': 'फर्मवेयर संस्करण',
    'deviceRegistry.addDevice.clusterId': 'क्लस्टर ID',
    'deviceRegistry.addDevice.trustScore': 'ट्रस्ट स्कोर (0-100)',
    'deviceRegistry.addDevice.location': 'स्थान',
    'deviceRegistry.addDevice.create': 'डिवाइस बनाएं',
    'deviceRegistry.editDevice.title': 'डिवाइस को संपादित करें',
    'deviceRegistry.editDevice.description': 'डिवाइस जानकारी अपडेट करें',
    'deviceRegistry.editDevice.update': 'डिवाइस अपडेट करें',
    'deviceRegistry.viewDevice.title': 'डिवाइस विवरण',
    'deviceRegistry.viewDevice.description': 'डिवाइस के लिए पूर्ण जानकारी',
    'deviceRegistry.pagination.showing': 'डिवाइस दिखा रहे हैं',
    'deviceRegistry.pagination.page': 'पृष्ठ',
    'deviceRegistry.loading': 'डिवाइस लोड हो रहे हैं...',
    'deviceRegistry.empty': 'कोई डिवाइस नहीं मिला। अपने फ़िल्टर को समायोजित करने का प्रयास करें या सुनिश्चित करें कि IoT सेवा चल रही है।',
    'deviceRegistry.toast.deviceCreated': 'डिवाइस सफलतापूर्वक बनाया गया',
    'deviceRegistry.toast.deviceUpdated': 'डिवाइस सफलतापूर्वक अपडेट किया गया',
    'deviceRegistry.toast.deviceDeleted': 'डिवाइस सफलतापूर्वक हटाया गया',
    'deviceRegistry.toast.loadFailed': 'डिवाइस लोड करने में विफल। कृपया सुनिश्चित करें कि IoT सेवा चल रही है।',

    // PROFILE MODULE
    'profile.title': 'उपयोगकर्ता प्रोफ़ाइल',
    'profile.subtitle': 'अपनी खाता जानकारी और प्राथमिकताओं का प्रबंधन करें',
    'profile.editProfile': 'प्रोफ़ाइल संपादित करें',
    'profile.editableSection': 'संपादन योग्य जानकारी',
    'profile.name': 'पूरा नाम',
    'profile.locale': 'भाषा/लोकेल',
    'profile.readOnlySection': 'खाता जानकारी (केवल पढ़ने के लिए)',
    'profile.phone': 'फ़ोन नंबर',
    'profile.email': 'ईमेल पता',
    'profile.userId': 'उपयोगकर्ता ID',
    'profile.externalId': 'बाहरी ID',
    'profile.kycStatus': 'KYC स्थिति',
    'profile.accountStatus': 'खाता स्थिति',
    'profile.lastLogin': 'अंतिम लॉगिन',
    'profile.createdAt': 'खाता बनाया गया',
    'profile.roles': 'भूमिकाएं',
    'profile.permissions': 'अनुमतियां',
    'profile.governmentAccess': 'सरकारी पहुंच',
    'profile.noRolesAssigned': 'कोई भूमिका निर्दिष्ट नहीं',
    'profile.noPermissionsAssigned': 'कोई अनुमति निर्दिष्ट नहीं',
    'profile.noGovernmentAccess': 'कोई सरकारी पहुंच नहीं',
    'profile.form.save': 'परिवर्तन सहेजें',
    'profile.form.cancel': 'रद्द करें',
    'profile.form.saving': 'सहेज रहे हैं...',
    'profile.locale.en': 'English (en)',
    'profile.locale.hi': 'हिंदी (hi)',
    'profile.locale.gu': 'ગુજરાતી (gu)',
    'profile.locale.mr': 'मराठी (mr)',
    'profile.loading': 'प्रोफ़ाइल लोड हो रहा है...',
    'profile.error.title': 'प्रोफ़ाइल लोड करने में त्रुटि',
    'profile.error.retry': 'पुनः प्रयास करें',
    'profile.toast.updated': 'प्रोफ़ाइल सफलतापूर्वक अपडेट की गई!',
    'profile.toast.error': 'प्रोफ़ाइल अपडेट करने में विफल',

    // REPORTS MODULE
    'reports.title': 'राजस्व और कार्बन रिपोर्ट',
    'reports.subtitle': 'व्यापक वित्तीय और पर्यावरणीय प्रभाव रिपोर्टिंग',
    'reports.notAvailable': 'रिपोर्ट उपलब्ध नहीं',
    'reports.message': 'राजस्व और परिचालन रिपोर्ट के लिए रिपोर्टिंग सेवा अंतिम बिंदुओं की आवश्यकता है।',

    // CONFIGURATION MODULE
    'configuration.title': 'सिस्टम कॉन्फ़िगरेशन',
    'configuration.subtitle': 'प्लेटफ़ॉर्म सेटिंग्स, एकीकरण और सुरक्षा नीतियों का प्रबंधन करें',
    'configuration.notAvailable': 'कॉन्फ़िगरेशन उपलब्ध नहीं',
    'configuration.message': 'सिस्टम कॉन्फ़िगरेशन के लिए सरकारी सेवा अंतिम बिंदुओं की आवश्यकता है।',

    // Locale Options
    'locale.en': 'अंग्रेजी',
    'locale.hi': 'हिंदी (हिंदी)',
    'locale.mr': 'मराठी (मराठी)',
    'locale.gu': 'गुजराती (ગુજરાતી)'
  }
};

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>('en');

  useEffect(() => {
    // Load language from localStorage
    const savedLanguage = localStorage.getItem('saubhagya_language') as Language;
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'hi')) {
      setLanguageState(savedLanguage);
    }
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('saubhagya_language', lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
